import React, { useState, useEffect, useContext } from 'react';
import { Grid, Box, Typography, Card, Container, CardContent } from '@mui/material';
import { collection, getDocs, Timestamp, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import AppContext from '../AppContext';
import { startOfDay, endOfDay } from "date-fns";
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import { format, isValid } from "date-fns";
import { styled } from '@mui/material/styles';

const DailyIncomeReport = () => {
  const [financialData, setFinancialData] = useState([]);
  const [fromDate, setFromDate] = useState(new Date());
  const [toDate, setToDate] = useState(new Date());
  const { shopId, language, t = (key) => key } = useContext(AppContext);

  const SummaryCard = styled(Card)(({ theme }) => ({
    borderRadius: '12px',
  
  
  }));
  
  const CardTitle = styled(Typography)(({ theme }) => ({
    fontWeight: 600,
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(2),
    borderBottom: `2px solid ${theme.palette.primary.light}`,
    paddingBottom: theme.spacing(1),
  }));
  
  const AmountText = styled(Typography)(({ theme }) => ({
    fontSize: '1.2rem',
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(1),
    '&:last-child': {
      marginBottom: 0,
    },
  }));

  useEffect(() => {
    const fetchData = async () => {
      try {
        const cashBookRef = collection(db, 'cashbook');
        const qCashbook = query(
          cashBookRef,
          where('timestamp', '>=', Timestamp.fromDate(startOfDay(fromDate))),
          where('timestamp', '<=', Timestamp.fromDate(endOfDay(toDate || fromDate))),
          where('shopId', '==', shopId)
        );

        const cashbookSnapshot = await getDocs(qCashbook);
        const financeData = cashbookSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        setFinancialData(financeData);
      } catch (error) {
        console.error('Error fetching data: ', error);
      }
    };

    fetchData();
  }, [fromDate, toDate, shopId]);

  const formatDate = (timestamp) => {
      const date = new Date(timestamp.toDate());
      if (isValid(date)) {
        return format(date, "dd-MM-yy h:mm a");
      }
      return "Invalid Date";
    };

  const currencyFormatter = new Intl.NumberFormat('en-US', {
    style: 'decimal',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

  // Summary Calculations
  const summary = financialData.reduce((acc, curr) => {
    const amount = curr.amount;
    if (curr.transactionType === 'income') {
      acc.income.total += amount;
      acc.income[curr.paymentMethod] = (acc.income[curr.paymentMethod] || 0) + amount;
    } else if (curr.transactionType === 'outcome' || curr.transactionType === 'refund') {
      acc.expense.total += amount;
      acc.expense[curr.paymentMethod] = (acc.expense[curr.paymentMethod] || 0) + amount;
    }
    return acc;
  }, {
    income: { total: 0, Cash: 0, Kpay: 0, 'Wave Pay': 0 },
    expense: { total: 0, Cash: 0, Kpay: 0, 'Wave Pay': 0 }
  });

  const balance = {
    total: summary.income.total - summary.expense.total,
    Cash: summary.income.Cash - summary.expense.Cash,
    Kpay: summary.income.Kpay - summary.expense.Kpay,
    'Wave Pay': summary.income['Wave Pay'] - summary.expense['Wave Pay']
  };

  const columns = [
    { field: 'orderId', headerName: t('ID'), width: 90 },
    { field: 'timestamp', headerName: t('Date'), width: 150 }, // No valueFormatter needed
    { field: 'description', headerName: t('Description'), width: 300 },
    {
      field: 'amount',
      headerName: t('Amount'),
      width: 130,
    },
    { field: 'paymentMethod', headerName: t('Payment Method'), width: 150 },
  ];
  
  // Prepare rows with pre-formatted timestamp
  const incomeRows = financialData
    .filter(item => item.transactionType === 'income')
    .map(item => ({
      ...item,
      orderId: item.orderId.toString().padStart(6, '0'),
      timestamp: formatDate(item.timestamp), // Format the timestamp here
       amount: currencyFormatter.format(item.amount),
    }));
  
  const expenseRows = financialData
    .filter(item => item.transactionType === 'outcome' || item.transactionType === 'refund')
    .map(item => ({
      ...item,
      orderId: item.orderId.toString().padStart(6, '0'),
      timestamp: formatDate(item.timestamp), // Format the timestamp here
      amount: currencyFormatter.format(item.amount),
    }));

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container>
        <Box>
          <Typography variant="h4" mb={2} gutterBottom>{t('Daily Income Report')}</Typography>
          
          {/* Date Pickers */}
          <Grid container spacing={2} sx={{ mb: 4 }}>
            <Grid item>
              <DatePicker
                label={t('From Date')}
                value={fromDate}
                onChange={(newValue) => setFromDate(newValue)}
                format="dd/MM/yyyy"
              />
            </Grid>
            <Grid item>
              <DatePicker
                label={t('To Date')}
                value={toDate}
                onChange={(newValue) => setToDate(newValue)}
                format="dd/MM/yyyy"
              />
            </Grid>
          </Grid>

          <Grid container spacing={3} sx={{ mb: 4 }}>
            {[
                {
                title: t('Income'),
                data: summary.income,
                color: '#2ecc71',
                },
                {
                title: t('Expense'),
                data: summary.expense,
                color: '#e74c3c',
                },
                {
                title: t('Balance'),
                data: balance,
                color: '#3498db',
                },
            ].map((item, index) => (
                <Grid item xs={12} md={4} key={index}>
                <SummaryCard>
                    <CardContent sx={{ p: 3 }}>
                    <CardTitle variant="h4">
                        {item.title}
                    </CardTitle>
                    <Box>
                        <AmountText>
                        <small>{t('Total')}:</small> {currencyFormatter.format(item.data.total)} MMK
                        </AmountText>
                        <AmountText>
                        <small>{t('Cash')}:</small> {currencyFormatter.format(item.data.Cash)} MMK
                        </AmountText>
                        <AmountText>
                        <small>{t('Kpay')}:</small> {currencyFormatter.format(item.data.Kpay)} MMK
                        </AmountText>
                        <AmountText>
                        <small>{t('Wave Pay')}:</small> {currencyFormatter.format(item.data['Wave Pay'])} MMK
                        </AmountText>
                    </Box>
                    </CardContent>
                </SummaryCard>
                </Grid>
            ))}
            </Grid>

          {/* Tables */}
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>{t('Income Transactions')}</Typography>
              <Box sx={{ height: 400, width: '100%' }}>
                <DataGrid
                  rows={incomeRows}
                  columns={columns}
                  slots={{ toolbar: GridToolbar }}
                  slotProps={{
                    toolbar: {
                      showQuickFilter: true,
                      quickFilterProps: { debounceMs: 500 },
                    },
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>{t('Expense Transactions')}</Typography>
              <Box sx={{ height: 400, width: '100%' }}>
                <DataGrid
                  rows={expenseRows}
                  columns={columns}
                  slots={{ toolbar: GridToolbar }}
                  slotProps={{
                    toolbar: {
                      showQuickFilter: true,
                      quickFilterProps: { debounceMs: 500 },
                    },
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Container>
    </LocalizationProvider>
  );
};

export default DailyIncomeReport;